import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router';
import { RoutedComponentProps } from 'src/app-routes';
import { Dispatch } from 'src/components-bl/types';
import { Button } from 'src/components-dummy';
import { CustomInspirationsGallery, GalleryType } from 'src/services';
import { IApiKey } from 'src/services/src/service/types/shops/api-keys';
import { GalleryPreview, EditGallerySideBar } from './components';
import {
  EditCustomInspirationsGalleryPageStyled,
  PreviewFooter,
  PreviewLayout,
  SideBarLayout,
  OverlayModalStyled,
} from './EditCustomInspirationsGalleryPage.styles';
import { GalleryMapper } from './mapper';
import { CustomInspirationGalleryDraft } from './types';
import { ExpandableSection } from '../hooks/useExpandableOptions';
import { getQueryStringParams } from '../common/utils';
import {
  Action,
  DirtyFormConfirmationDialog,
} from '../components/DirtyFormConfirmationDialog/DirtyFormConfirmationDialog';
import { editGalleryPageActions } from '../../Actions';
import { useEditGalleryPage } from '../../useEditGalleryPage';

interface EditCustomInspirationsGalleryPageProps extends RoutedComponentProps {
  shopId: number;
  dispatch: Dispatch;
  galleryId: string;
  gallery?: CustomInspirationsGallery;
  hasDraft: boolean;
  shopApiKey?: IApiKey;
  allowedToAddImages: number | undefined;
}

export function EditCustomInspirationsGalleryPage({
  shopId,
  dispatch,
  galleryId,
  gallery,
  hasDraft,
  shopApiKey,
  permittedRouteMap,
  allowedToAddImages,
}: EditCustomInspirationsGalleryPageProps): JSX.Element {
  const mappedInitialGallery = useMemo(() => GalleryMapper.mapGalleryToDraft(gallery), [gallery]);

  const [draftGallery, setDraftGallery] = useState<CustomInspirationGalleryDraft | undefined>(
    mappedInitialGallery
  );
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);

  const onChange = useCallback(
    (partialGallery: Partial<CustomInspirationGalleryDraft>) => {
      if (draftGallery) {
        setDraftGallery({ ...draftGallery, ...partialGallery });
      }
    },
    [draftGallery, setDraftGallery]
  );

  const onSaveGallery = useCallback(
    async galleryToSave => {
      let updatedGallery;

      try {
        setIsSaveInProgress(true);

        updatedGallery = await (
          dispatch(
            editGalleryPageActions.updateGallery({
              shopId,
              galleryId,
              payload: galleryToSave,
            })
          ) as any
        ).unwrap();
      } catch (error) {
        console.error(error);
      }

      setIsSaveInProgress(false);

      if (updatedGallery) {
        return GalleryMapper.mapGalleryToDraft(updatedGallery);
      }
    },
    [dispatch, shopId, galleryId, setDraftGallery]
  );

  const {
    navigateToList,
    resetDraftAndCallback,
    onSaveDraftGallery,
    startTagsDetection,
    saveDraftAndCallback,
    applyAllSuggestedTags,
    removeAllSuggestedTags,
    triggerTheModalIfDirtyOrCallback,
    showDirtyModal,
    currentActionConfig,
    closeModal,
    isDirty,
  } = useEditGalleryPage({
    shopId,
    dispatch,
    galleryId,
    mappedInitialGallery,
    permittedRouteMap,
    onSaveGallery,
    draftGallery,
    setDraftGallery,
  });

  const navigateToAddImages = useCallback(() => {
    if (!permittedRouteMap.addImagesToCustomInspirationsGallery) return;

    dispatch(
      editGalleryPageActions.navigateTo({
        navigateTo: generatePath(permittedRouteMap.addImagesToCustomInspirationsGallery.path, {
          shopId,
          galleryId,
        }),
      })
    );
  }, [shopId, permittedRouteMap.addImagesToCustomInspirationsGallery, dispatch, galleryId]);

  const navigateToImageSettings = useCallback(
    (imageId: string, openedSections: ExpandableSection[]) => {
      if (!permittedRouteMap.tagImageInCustomInspirationsGallery) {
        return;
      }

      const baseUrl = generatePath(permittedRouteMap.tagImageInCustomInspirationsGallery.path, {
        shopId,
        galleryId,
        imageId,
      });

      const queryParams = getQueryStringParams(openedSections);

      const targetUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

      dispatch(
        editGalleryPageActions.navigateTo({
          navigateTo: targetUrl,
        })
      );
    },
    [dispatch, permittedRouteMap.tagImageInCustomInspirationsGallery, galleryId, shopId]
  );

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        await (
          dispatch(
            editGalleryPageActions.getGallery({
              shopId,
              galleryId,
              galleryType: GalleryType.CustomInspirations,
            })
          ) as any
        ).unwrap();
      } catch (error) {
        console.error(error);
        navigateToList();
      }
    };

    fetchGallery();
    dispatch(editGalleryPageActions.getApiKeys({ shopId }));

    return () => {
      dispatch(editGalleryPageActions.resetCurrentGallery());
      dispatch(editGalleryPageActions.resetApiKeys());
    };
  }, [shopId, galleryId, dispatch, navigateToList]);

  const resetDraftToInitialState = useCallback(() => {
    setDraftGallery(mappedInitialGallery);
  }, [setDraftGallery, mappedInitialGallery]);

  useEffect(() => {
    resetDraftToInitialState();
  }, [resetDraftToInitialState]);

  return (
    <OverlayModalStyled show fullscreen={!hasDraft}>
      <EditCustomInspirationsGalleryPageStyled>
        <SideBarLayout>
          <EditGallerySideBar
            gallery={draftGallery}
            onChange={onChange}
            galleryId={galleryId}
            dispatch={dispatch}
            navigateToList={navigateToList}
          />
        </SideBarLayout>
        <PreviewLayout>
          <GalleryPreview
            gallery={draftGallery}
            galleryId={galleryId}
            onChange={onChange}
            navigateToImageSettings={navigateToImageSettings}
            navigateToAddImages={navigateToAddImages}
            dispatch={dispatch}
            shopId={shopId}
            shopApiKey={shopApiKey}
            allowedToAddImages={allowedToAddImages}
            startTagsDetection={() =>
              triggerTheModalIfDirtyOrCallback(startTagsDetection, Action.Generate)
            }
            applyAllSuggestedTags={() =>
              triggerTheModalIfDirtyOrCallback(applyAllSuggestedTags, Action.AcceptTags)
            }
            removeAllSuggestedTags={() =>
              triggerTheModalIfDirtyOrCallback(removeAllSuggestedTags, Action.RejectTags)
            }
          />
          <PreviewFooter>
            <Button variant='secondary' onClick={navigateToList}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={onSaveDraftGallery}
              disabled={!isDirty}
              loading={isSaveInProgress}
            >
              Save Gallery
            </Button>
          </PreviewFooter>
        </PreviewLayout>
      </EditCustomInspirationsGalleryPageStyled>
      <DirtyFormConfirmationDialog
        action={currentActionConfig?.action}
        close={closeModal}
        isOpened={showDirtyModal}
        isLoading={isSaveInProgress}
        onResetDraftAndProceed={resetDraftAndCallback}
        onSaveAndProceed={saveDraftAndCallback}
      />
    </OverlayModalStyled>
  );
}
